<template>
  <RegisterForm
    :title="$t('verificationStatus.personalInformation')"
    :desc="$t('verificationStatus.openAccount.personalInformation.desc')"
    :nextButtonOnClick="nextButtonOnClick"
    verticalPadding="py-0"
  >
    <el-form class="mt-10.5 grid" :model="form" :rules="rules" ref="form">
      <FormRowContainer>
        <InputForm
          v-model="form.firstName"
          name="firstName"
          :label="$t('register.personalDetails.page1.firstName')"
          :placeholder="$t('register.personalDetails.page1.firstName')"
          :disabled="disabled"
          maxlength="128"
        />
        <InputForm
          v-model="form.middleName"
          name="middleName"
          :label="$t('register.personalDetails.page1.middleName')"
          :placeholder="$t('register.personalDetails.page1.middleName')"
          :disabled="disabled"
          maxlength="128"
        ></InputForm>
      </FormRowContainer>
      <FormRowContainer>
        <InputForm
          v-model="form.lastName"
          name="lastName"
          :label="$t('register.personalDetails.page1.lastName')"
          :placeholder="$t('register.personalDetails.page1.lastName')"
          :disabled="disabled"
          maxlength="128"
        />
        <SelectForm
          v-model="form.gender"
          name="gender"
          class="showPlaceholder"
          :label="$t('register.personalDetails.page1.userGender')"
          :disabled="disabled"
        >
          <!-- Remains en for ja -->
          <el-option
            v-for="(item, index) in genderList"
            :key="index"
            :label="lang === 'ja' ? $t(item.label, 'en') : $t(item.label)"
            :value="item.value"
          ></el-option>
        </SelectForm>
      </FormRowContainer>
      <FormRowContainer>
        <el-form-item :label="$t('register.personalDetails.page1.dob')" class="finish" prop="dob" required>
          <div class="w-full flex items-center justify-start gap-4">
            <SelectForm
              v-model="dateOfBirth.date"
              :placeholder="$t('register.personalDetails.page1.date')"
              :disabled="disabled"
              testId="day"
              class="showPlaceholder"
            >
              <el-option
                v-for="(item, index) in days"
                :key="index"
                :label="item"
                :value="item"
                :data-testid="'date' + item"
              ></el-option>
            </SelectForm>
            <SelectForm
              v-model="dateOfBirth.month"
              :placeholder="$t('register.personalDetails.page1.month')"
              :disabled="disabled"
              testId="month"
              class="showPlaceholder"
            >
              <el-option
                v-for="month in months"
                :key="month"
                :label="month"
                :value="month"
                :data-testid="'month' + month"
              ></el-option>
            </SelectForm>
            <SelectForm
              v-model="dateOfBirth.year"
              :placeholder="$t('register.personalDetails.page1.year')"
              :disabled="disabled"
              testId="year"
              class="showPlaceholder"
            >
              <el-option v-for="item in years" :key="item" :label="item" :value="item" :data-testid="item"></el-option>
            </SelectForm>
          </div> </el-form-item
        ><SelectForm
          v-model="form.nationalityId"
          :label="$t('register.personalDetails.page1.nationality')"
          :placeholder="$t('register.personalDetails.page1.nationality')"
          :disabled="disabled"
          class="showPlaceholder"
          name="nationalityId"
        >
          <el-option
            v-for="(item, index) in nationalities"
            :key="index"
            :label="item.nationality"
            :value="item.id"
            :data-testid="item.id < 10 ? 'nationality0' + item.id : 'nationality' + item.id"
          ></el-option> </SelectForm
      ></FormRowContainer>
      <FormRowContainer>
        <el-form-item :label="$t('register.personalDetails.page1.phoneNum')" class="finish" prop="mobile" required>
          <div class="w-full flex items-center justify-start gap-4">
            <SelectForm
              :disabled="disabled"
              class="flex-[25] showPlaceholder"
              testId="phoneCode"
              v-model="form.phoneCode"
              placeholder="00"
            >
              <el-option
                v-for="(item, index) in phoneCodes"
                :key="index"
                :label="item"
                :value="item"
                :data-testid="'phoneCode' + item"
              ></el-option>
            </SelectForm>
            <InputForm
              class="flex-[65] showPlaceholder special_input"
              testId="mobile"
              :value="formattedMobile"
              :placeholder="'Phone'"
              :disabled="disabled"
              @input="mobileOnChanged"
            /></div
        ></el-form-item>
        <InputForm
          v-model="form.email"
          name="email"
          :label="$t('register.personalDetails.page1.email')"
          :placeholder="$t('register.personalDetails.page1.email')"
          :disabled="isEmailDisabled || disabled"
        />
      </FormRowContainer> </el-form
  ></RegisterForm>
</template>

<script>
import { mapState } from 'vuex';
import { validateEmail, validateNumber } from '@/util/validation';
import { endAjaxLoading, startLoading } from '@/util/loading';
import registerStep, { getUserStep } from '@/constants/register/registerStep';
import RegisterForm from '@/components/register/registerForms/RegisterForm.vue';
import FormRowContainer from '@/components/register/registerForms/formRows/FormRowContainer.vue';
import registerRepository from '@/repositories/register/registerRepository';
import { SENSORS_TRACK } from '@/util/sensorsdata/constant';
import { sensorsTrack } from '@/util/sensorsdata';

export default {
  name: 'PersonalInformation',
  components: { RegisterForm, FormRowContainer },
  data() {
    return {
      // For app registered user no have email
      // Default disabled to avoid API failed (email input enabled)
      isEmailDisabled: true,
      dateOfBirth: {
        year: null,
        month: null,
        date: null
      },
      ndt: new Date(),
      form: {
        // title: null,
        gender: null,
        firstName: '',
        middleName: '',
        lastName: '',
        nationalityId: null,
        phoneCode: null,
        mobile: '',
        dob: null,
        email: ''
      },
      rules: {
        gender: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('common.formValidation.genderReq')
          }
        ],
        firstName: [
          {
            required: true,
            trigger: 'blur',
            message: this.$t('common.formValidation.firstNameReq')
          },
          {
            max: 50,
            message: this.$t('common.formValidation.noteMoreThan', {
              limit: 50
            })
          },
          {
            min: 1,
            message: this.$t('common.formValidation.noteLessThan', {
              limit: 1
            })
          }
        ],
        middleName: [
          {
            required: false,
            trigger: 'blur'
          },
          {
            max: 50,
            message: this.$t('common.formValidation.noteMoreThan', {
              limit: 50
            })
          }
        ],
        lastName: [
          {
            required: true,
            trigger: 'blur',
            message: this.$t('common.formValidation.lastNameReq')
          },
          {
            min: 1,
            message: this.$t('common.formValidation.noteLessThan', {
              limit: 1
            })
          },
          {
            max: 50,
            message: this.$t('common.formValidation.noteMoreThan', {
              limit: 50
            })
          }
        ],
        nationalityId: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('common.formValidation.nationalityReq')
          }
        ],
        mobile: [
          {
            validator: (rule, value, callback) => {
              if (!(this.form.phoneCode?.length > 0 && this.form.mobile?.length > 0)) {
                callback(new Error(this.$t('common.formValidation.mobReq')));
              }
              callback();
            },
            trigger: 'change'
          },
          {
            min: 4,
            message: this.$t('common.formValidation.noteLessThan', {
              limit: 4
            })
          },
          {
            max: 20,
            message: this.$t('common.formValidation.noteMoreThan', {
              limit: 20
            })
          },
          {
            validator: validateNumber,
            trigger: 'blur'
          }
        ],
        dob: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('common.formValidation.dobReq')
          }
        ],
        email: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('common.formValidation.emailReq')
          },
          {
            validator: validateEmail,
            trigger: 'change'
          }
        ]
      }
    };
  },
  computed: {
    disabled() {
      return this.currStep > registerStep.personalInformation;
    },
    years() {
      const year = this.ndt.getFullYear() - 18;
      return Array.from({ length: 101 - 18 }, (value, index) => year - index);
    },
    months() {
      if (this.dateOfBirth.year == this.years[0]) {
        const month = this.ndt.getMonth() + 1;
        return Array.from({ length: month }, (value, index) => this.formatDobOption(index + 1));
      } else if (this.dateOfBirth.year == this.years[this.years.length - 1]) {
        const month = this.ndt.getMonth() + 1;
        return Array.from({ length: 12 - month + 1 }, (value, index) => this.formatDobOption(month + index));
      } else {
        return Array.from({ length: 12 }, (value, index) => this.formatDobOption(index + 1));
      }
    },
    days() {
      let date;
      if (this.dateOfBirth.year == this.years[0] && this.dateOfBirth.month == this.ndt.getMonth() + 1) {
        date = this.ndt.getDate();
        return Array.from({ length: date }, (value, index) => this.formatDobOption(index + 1));
      } else if (
        this.dateOfBirth.year == this.years[this.years.length - 1] &&
        this.dateOfBirth.month == this.ndt.getMonth() + 1
      ) {
        date = this.ndt.getDate();
        let length = new Date(this.dateOfBirth.year, this.dateOfBirth.month, 0).getDate();
        return Array.from({ length: length - date + 1 }, (value, index) => this.formatDobOption(date + index));
      } else {
        date = new Date(this.dateOfBirth.year, this.dateOfBirth.month, 0).getDate();
        return Array.from({ length: date }, (value, index) => this.formatDobOption(index + 1));
      }
    },
    // defaultTitle() {
    //   return (this.form.title = this.titleList?.[0]?.value ?? null);
    // },
    defaultGender() {
      return (this.form.gender = this.genderList?.[0]?.value ?? null);
    },
    lang() {
      return this.$store.state.common.lang;
    },
    ...mapState('register', ['currStep', 'nationalities', 'phoneCodes']),
    // For validation
    mobileNumber() {
      return [this.form.phoneCode, this.form.mobile];
    },
    formattedMobile() {
      return this.formatMobile(this.form.mobile);
    }
  },
  watch: {
    mobileNumber() {
      this.$refs.form?.validateField('mobile');
    },
    dateOfBirth: {
      handler(dob) {
        const presMonth = this.months.find(f => f == dob.month);
        const presDate = this.days.find(f => f == dob.date);
        if (!presMonth) dob.month = '';
        if (!presDate) dob.date = '';
        this.form.dob = dob.year && dob.month && dob.date ? `${dob.year}-${dob.month}-${dob.date}` : '';
        if (this.form.dob?.length > 0) {
          this.$refs.form?.validateField('dob');
        }
      },
      deep: true
    },
    lang() {
      this.$store.dispatch('register/getNationalities', this.lang);
    }
  },
  created() {
    this.titleList = [
      { value: 'Mr', label: 'register.personalDetails.page1.titleDropdown.mr' },
      { value: 'Mrs', label: 'register.personalDetails.page1.titleDropdown.mrs' },
      { value: 'Ms', label: 'register.personalDetails.page1.titleDropdown.ms' },
      { value: 'Miss', label: 'register.personalDetails.page1.titleDropdown.miss' },
      { value: 'Dr', label: 'register.personalDetails.page1.titleDropdown.dr' },
      { value: 'Prof', label: 'register.personalDetails.page1.titleDropdown.prof' }
    ];

    this.genderList = [
      { value: 'Male', label: 'register.personalDetails.page1.genderDropdown.male' },
      { value: 'Female', label: 'register.personalDetails.page1.genderDropdown.female' }
    ];
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      const responses = await Promise.all([
        registerRepository.getData(1),
        this.$store.dispatch('register/getPhoneCodes'),
        this.$store.dispatch('register/getNationalities', this.lang)
      ]);
      const userData = responses[0];

      if (userData instanceof Error) {
        this.form.gender = this.defaultGender ?? null;
        this.$message({
          message: userData.message,
          type: 'error'
        });
      } else {
        const { gender, firstName, middleName, lastName, nationalityId, phoneCode, mobile, dob, email } = userData;

        this.form = {
          ...this.form,
          gender: gender ?? this.defaultGender ?? null,
          firstName: firstName ?? '',
          middleName: middleName ?? '',
          lastName: lastName ?? '',
          nationalityId: nationalityId ?? null,
          phoneCode: phoneCode ?? null,
          mobile: mobile ?? '',
          dob: dob ?? null,
          email: email ?? ''
        };

        if (!email) {
          this.isEmailDisabled = false;
        }

        // clearValidate after init data
        this.$nextTick(() => this.$refs.form?.clearValidate());

        if (dob) {
          const splitDob = dob.split('-');
          this.dateOfBirth = {
            year: parseInt(splitDob[0]),
            month: this.formatDobOption(splitDob[1]),
            date: this.formatDobOption(splitDob[2])
          };
          // nextTick to let validateField work after dateOfBirth watcher changed dob
          // (not working in watcher in this case)
          // validate to prevent dob not in range
          this.$nextTick(() => this.$refs.form?.validateField('dob'));
        }
      }
    },
    mobileOnChanged(input) {
      this.form.mobile = input?.replaceAll?.(/\s/g, '') ?? '';
    },
    formatDobOption(option) {
      return option.toString().padStart(2, '0');
    },
    formatMobile(mobile) {
      return mobile?.replace?.(/(.{4})/g, '$1 ')?.replace(/\s$/g, '') ?? '';
    },
    nextButtonOnClick() {
      sensorsTrack(SENSORS_TRACK.EVENT_NAME.OPEN_IDENTITY_PAGE_CLICK, {
        openidentity_level: SENSORS_TRACK.IDENTITY_LEVEL.LV1,
        openidentity_step: SENSORS_TRACK.IDENTITY_STEP.LV1_PERSONAL_INFORMATION,
        button_name: SENSORS_TRACK.BUTTON_NAME.LV1_NEXT
      });
      if (this.disabled) {
        this.$store.dispatch('register/setActiveTab', registerStep.accountConfiguration);
      } else {
        this.$refs.form?.validate(async valid => {
          if (valid) {
            startLoading('ajax');
            const { email, ...restForm } = this.form;

            const response = await registerRepository.process({
              step: getUserStep(registerStep.personalInformation),
              form: {
                ...restForm,
                ...(!this.isEmailDisabled && { email: email })
              }
            });

            if (response instanceof Error) {
              this.$message({
                message: response.message,
                type: 'error'
              });
            } else {
              const usernameResponse = await registerRepository.getUsername();
              if (usernameResponse instanceof Error) {
                this.$message({
                  message: usernameResponse.message,
                  type: 'error'
                });
              } else {
                this.$store.commit('common/setUserName', usernameResponse);
                this.$store.commit('common/setIndividualName', usernameResponse.split('&')[0].trim());
                this.$store.dispatch('register/setStep', registerStep.accountConfiguration);
              }
            }

            endAjaxLoading();
          } else {
            console.log('error submit!!');
          }
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// For .finish
@import '@/assets/css/components/input.scss';
.special_input {
  /deep/ .el-input__inner {
    direction: ltr;
    @include rtl-sass-value(text-align, left, right);
  }
}
</style>
